import { useState, useEffect } from 'react';
import { User, MapPin, Save, Settings, Building } from 'lucide-react';
import { updateUserAttributes } from 'aws-amplify/auth';
import toast from 'react-hot-toast';

import SMSOptIn from '../components/SMSOptIn';
import { colors, radii, shadows, typography, fontWeights, spacing } from '../styles/designTokens';

const ProfilePage = ({ user, userAttributes }) => {
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [homeAddress, setHomeAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });
  const [addressValidation, setAddressValidation] = useState({
    isValid: null,
    message: '',
    testing: false
  });
  const [travelerInfo, setTravelerInfo] = useState({
    tsaPreCheck: false,
    clear: false,
    globalEntry: false
  });
  const [loungeMemberships, setLoungeMemberships] = useState({
    admiralsClub: false,
    skyClub: false,
    unitedClub: false,
    unitedPolaris: false,
    centurionLounge: false,
    chaseSapphire: false
  });

  useEffect(() => {
    // Load phone number from Cognito user attributes (primary source)
    if (userAttributes?.phone_number) {
      setPhoneNumber(userAttributes.phone_number);
    }
    // Note: Removed localStorage fallback to prevent user isolation issues

    // Load combined profile data from Cognito address attribute
    if (userAttributes?.address) {
      try {
        const profileData = JSON.parse(userAttributes.address);
        
        // Extract home address fields
        setHomeAddress(prev => ({
          ...prev,
          street: profileData.street || '',
          city: profileData.city || '',
          state: profileData.state || '',
          zipCode: profileData.zipCode || '',
          country: profileData.country || 'US'
        }));
        
        // Extract traveler info fields
        setTravelerInfo(prev => ({
          ...prev,
          tsaPreCheck: profileData.tsaPreCheck || false,
          clear: profileData.clear || false,
          globalEntry: profileData.globalEntry || false
        }));

        // Extract lounge membership fields
        setLoungeMemberships(prev => ({
          ...prev,
          admiralsClub: profileData.admiralsClub || false,
          skyClub: profileData.skyClub || false,
          unitedClub: profileData.unitedClub || false,
          unitedPolaris: profileData.unitedPolaris || false,
          centurionLounge: profileData.centurionLounge || false,
          chaseSapphire: profileData.chaseSapphire || false
        }));
      } catch (error) {
        console.error('Error parsing profile data from Cognito:', error);
      }
    }
    // Note: Removed localStorage fallback to prevent user isolation issues
    
  }, [user, userAttributes]);

  const handleAddressChange = (field, value) => {
    setHomeAddress(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation when user starts typing
    if (addressValidation.isValid !== null) {
      setAddressValidation({
        isValid: null,
        message: '',
        testing: false
      });
    }
  };

  const handleTravelerInfoChange = (field, value) => {
    setTravelerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLoungeMembershipChange = (field, value) => {
    setLoungeMemberships(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateAddress = () => {
    const { street, city, state, zipCode } = homeAddress;
    
    if (!street.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
      return {
        isValid: false,
        message: 'All address fields are required'
      };
    }
    
    // Validate ZIP code format (US: 12345 or 12345-6789)
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zipCode.trim())) {
      return {
        isValid: false,
        message: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)'
      };
    }
    
    return {
      isValid: true,
      message: 'Address format looks good'
    };
  };

  const testAddress = async () => {
    const validation = validateAddress();
    if (!validation.isValid) {
      setAddressValidation(validation);
      return;
    }
    
    setAddressValidation({ isValid: null, message: '', testing: true });
    
    try {
      // For now, just simulate address validation
      // In production, you'd call a geocoding service here
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAddressValidation({
        isValid: true,
        message: 'Address verified successfully!',
        testing: false
      });
    } catch (error) {
      setAddressValidation({
        isValid: false,
        message: 'Could not verify address. Please check and try again.',
        testing: false
      });
    }
  };

  const formatPhoneNumberForCognito = (phoneNumber) => {
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Validate minimum length
    if (digits.length < 10) {
      throw new Error('Phone number must have at least 10 digits');
    }
    
    // If it starts with 1 and has 11 digits, it's a US number
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    
    // If it has 10 digits, assume it's a US number and add +1
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // If it already starts with +, return as is
    if (phoneNumber.startsWith('+')) {
      return phoneNumber;
    }
    
    // Otherwise, add + prefix
    return `+${digits}`;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const userAttributesToUpdate = {};
      
      // Save phone number to Cognito if it's provided
      if (phoneNumber && phoneNumber.trim() !== '') {
        try {
          const formattedPhoneNumber = formatPhoneNumberForCognito(phoneNumber.trim());
          userAttributesToUpdate.phone_number = formattedPhoneNumber;
        } catch (cognitoError) {
          console.error('Error formatting phone number:', cognitoError);
          toast.error('Phone number format is invalid. Please use format: +1 (555) 123-4567');
          setLoading(false);
          return;
        }
      }

      // Save combined profile data to Cognito address attribute
      const addressValidation = validateAddress();
      if (addressValidation.isValid) {
        try {
          // Combine home address, traveler info, and lounge memberships into one object
          const combinedProfileData = {
            // Home address fields
            street: homeAddress.street,
            city: homeAddress.city,
            state: homeAddress.state,
            zipCode: homeAddress.zipCode,
            country: homeAddress.country,
            // Traveler info fields
            tsaPreCheck: travelerInfo.tsaPreCheck,
            clear: travelerInfo.clear,
            globalEntry: travelerInfo.globalEntry,
            // Lounge membership fields
            admiralsClub: loungeMemberships.admiralsClub,
            skyClub: loungeMemberships.skyClub,
            unitedClub: loungeMemberships.unitedClub,
            unitedPolaris: loungeMemberships.unitedPolaris,
            centurionLounge: loungeMemberships.centurionLounge,
            chaseSapphire: loungeMemberships.chaseSapphire
          };
          
          const profileDataString = JSON.stringify(combinedProfileData);
          userAttributesToUpdate.address = profileDataString;
        } catch (profileError) {
          console.error('Error preparing profile data for Cognito:', profileError);
          toast.error('Profile data format is invalid. Please check all fields.');
          setLoading(false);
          return;
        }
      } else if (homeAddress.street || homeAddress.city || homeAddress.state || homeAddress.zipCode) {
        // If user has entered partial address but it's invalid
        toast.error('Please complete and verify your address before saving.');
        setLoading(false);
        return;
      }

      // Update Cognito with all attributes at once
      if (Object.keys(userAttributesToUpdate).length > 0) {
        try {
          await updateUserAttributes({
            userAttributes: userAttributesToUpdate
          });
        } catch (cognitoError) {
          console.error('Error saving to Cognito:', cognitoError);
          toast.error('Failed to save to your account. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Note: Removed localStorage backup to prevent user isolation issues
      // All user profile data is now stored only in Cognito user attributes
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <User size={32} style={{ color: colors.urgent }} />
          <h1 style={{
            fontSize: '32px',
            fontWeight: fontWeights.bold,
            color: colors.textPrimary,
            margin: 0
          }}>
            Profile
          </h1>
        </div>
        <p style={{
          fontSize: typography.subheading.fontSize,
          color: colors.textSecondary,
          margin: 0
        }}>
          Manage your personal information and contact details
        </p>
      </div>

      {/* Contact Information */}
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: radii.card,
        padding: '24px',
        marginBottom: '24px',
        boxShadow: shadows.card
      }}>
        <h2 style={{
          fontSize: typography.heading.fontSize,
          fontWeight: fontWeights.semibold,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}>
          <Settings size={20} />
          Contact Information
        </h2>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: typography.body.fontSize,
            fontWeight: fontWeights.medium,
            color: colors.cardBodyText,
            marginBottom: '6px'
          }}>
            Email Address
          </label>
          <input
            type="email"
            value={userAttributes?.email || user?.signInDetails?.loginId || ''}
            disabled
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `1px solid ${colors.divider}`,
              borderRadius: radii.inner,
              fontSize: typography.subheading.fontSize,
              backgroundColor: colors.surfaceAlt,
              color: colors.textSecondary
            }}
          />
          <p style={{
            fontSize: typography.caption.fontSize,
            color: colors.textSecondary,
            margin: '6px 0 0 0'
          }}>
            Email address is managed through your account settings
          </p>
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: typography.body.fontSize,
            fontWeight: fontWeights.medium,
            color: colors.cardBodyText,
            marginBottom: '6px'
          }}>
            Phone Number (for SMS notifications)
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="5551234567"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `1px solid ${colors.divider}`,
              borderRadius: radii.inner,
              fontSize: typography.subheading.fontSize
            }}
          />
          <p style={{
            fontSize: typography.caption.fontSize,
            color: colors.textSecondary,
            margin: '6px 0 0 0'
          }}>
            Enter your 10-digit phone number for SMS notifications. Save your profile, then enable SMS below.
          </p>
        </div>
      </div>

      {/* SMS Opt-In Component */}
      <SMSOptIn 
        user={user}
        userAttributes={userAttributes}
        phoneNumber={phoneNumber}
        onStatusChange={(_hasConsent, _status) => {
        }}
      />

      {/* Home Address */}
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: radii.card,
        padding: '24px',
        marginBottom: '24px',
        boxShadow: shadows.card
      }}>
        <h2 style={{
          fontSize: typography.heading.fontSize,
          fontWeight: fontWeights.semibold,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}>
          <MapPin size={20} />
          Home Address
        </h2>
        <p style={{
          fontSize: typography.body.fontSize,
          color: colors.textSecondary,
          margin: '0 0 20px 0'
        }}>
          We'll use this to calculate when you should leave for the airport and send departure alerts.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              marginBottom: '6px'
            }}>
              Street Address *
            </label>
            <input
              type="text"
              value={homeAddress.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
              placeholder="123 Main Street"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              marginBottom: '6px'
            }}>
              City *
            </label>
            <input
              type="text"
              value={homeAddress.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              placeholder="Dallas"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize
              }}
            />
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              marginBottom: '6px'
            }}>
              State *
            </label>
            <input
              type="text"
              value={homeAddress.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              placeholder="TX"
              maxLength="2"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize,
                textTransform: 'uppercase'
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              marginBottom: '6px'
            }}>
              ZIP Code *
            </label>
            <input
              type="text"
              value={homeAddress.zipCode}
              onChange={(e) => handleAddressChange('zipCode', e.target.value)}
              placeholder="75201"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              marginBottom: '6px'
            }}>
              Country
            </label>
            <select
              value={homeAddress.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${colors.divider}`,
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize,
                backgroundColor: colors.surface
              }}
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
            </select>
          </div>
        </div>
        
        {/* Address Validation */}
        {addressValidation.message && (
          <div style={{
            padding: '12px',
            borderRadius: radii.inner,
            marginBottom: '16px',
            backgroundColor: addressValidation.isValid ? colors.successBackground : colors.urgentBackground,
            border: `1px solid ${addressValidation.isValid ? '#bbf7d0' : '#fecaca'}`,
            color: addressValidation.isValid ? '#166534' : colors.urgent
          }}>
            {addressValidation.message}
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={testAddress}
            disabled={addressValidation.testing}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1e40af',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              cursor: addressValidation.testing ? 'not-allowed' : 'pointer',
              opacity: addressValidation.testing ? 0.6 : 1
            }}
          >
            {addressValidation.testing ? 'Verifying...' : 'Test Address'}
          </button>
          
          <p style={{
            fontSize: typography.caption.fontSize,
            color: colors.textSecondary,
            margin: 0
          }}>
            Verify your address to enable departure alerts
          </p>
        </div>
      </div>

      {/* Traveler Information */}
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: radii.card,
        padding: '24px',
        marginBottom: '24px',
        boxShadow: shadows.card
      }}>
        <h2 style={{
          fontSize: typography.heading.fontSize,
          fontWeight: fontWeights.semibold,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}>
          <User size={20} />
          Traveler Information
        </h2>
        <p style={{
          fontSize: typography.body.fontSize,
          color: colors.textSecondary,
          margin: '0 0 20px 0'
        }}>
          Help us provide better travel recommendations and departure time estimates.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={travelerInfo.tsaPreCheck}
                onChange={(e) => handleTravelerInfoChange('tsaPreCheck', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              TSA PreCheck
            </label>
            <p style={{
              fontSize: typography.caption.fontSize,
              color: colors.textSecondary,
              margin: '6px 0 0 30px'
            }}>
              I have an active TSA PreCheck membership
            </p>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={travelerInfo.clear}
                onChange={(e) => handleTravelerInfoChange('clear', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              Clear
            </label>
            <p style={{
              fontSize: typography.caption.fontSize,
              color: colors.textSecondary,
              margin: '6px 0 0 30px'
            }}>
              I have an active Clear membership
            </p>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={travelerInfo.globalEntry}
                onChange={(e) => handleTravelerInfoChange('globalEntry', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              Global Entry
            </label>
            <p style={{
              fontSize: typography.caption.fontSize,
              color: colors.textSecondary,
              margin: '6px 0 0 30px'
            }}>
              I have an active Global Entry membership
            </p>
          </div>
        </div>
      </div>

      {/* Airport Lounges */}
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: radii.card,
        padding: '24px',
        marginBottom: '24px',
        boxShadow: shadows.card
      }}>
        <h2 style={{
          fontSize: typography.heading.fontSize,
          fontWeight: fontWeights.semibold,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}>
          <Building size={20} />
          Airport Lounges
        </h2>
        <p style={{
          fontSize: typography.body.fontSize,
          color: colors.textSecondary,
          margin: '0 0 20px 0'
        }}>
          Select your lounge memberships to receive personalized airport recommendations and lounge access information.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.admiralsClub}
                onChange={(e) => handleLoungeMembershipChange('admiralsClub', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              American Airlines Admirals Club
            </label>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.skyClub}
                onChange={(e) => handleLoungeMembershipChange('skyClub', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              Delta Sky Club
            </label>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.unitedClub}
                onChange={(e) => handleLoungeMembershipChange('unitedClub', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              United Club
            </label>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.unitedPolaris}
                onChange={(e) => handleLoungeMembershipChange('unitedPolaris', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              United Polaris Lounge
            </label>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.centurionLounge}
                onChange={(e) => handleLoungeMembershipChange('centurionLounge', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              Centurion Lounge
            </label>
          </div>
          
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.cardBodyText,
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={loungeMemberships.chaseSapphire}
                onChange={(e) => handleLoungeMembershipChange('chaseSapphire', e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: colors.urgent
                }}
              />
              Chase Sapphire Lounge
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px'
      }}>
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: colors.urgent,
            color: 'white',
            border: 'none',
            borderRadius: radii.inner,
            fontSize: typography.subheading.fontSize,
            fontWeight: fontWeights.semibold,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Save size={20} />
          {loading ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
